import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Plug, Receipt, LineChart } from "lucide-react";
import type { ReactNode } from "react";

interface Step {
    iconNode: ReactNode;
    titleKey: string;
    descKey: string;
    detailKey: string;
}

const STEPS: Step[] = [
    {
        iconNode: <Plug size={20} />,
        titleKey: "landing.how.step1Title",
        descKey: "landing.how.step1Desc",
        detailKey: "landing.how.step1Detail",
    },
    {
        iconNode: <Receipt size={20} />,
        titleKey: "landing.how.step2Title",
        descKey: "landing.how.step2Desc",
        detailKey: "landing.how.step2Detail",
    },
    {
        iconNode: <LineChart size={20} />,
        titleKey: "landing.how.step3Title",
        descKey: "landing.how.step3Desc",
        detailKey: "landing.how.step3Detail",
    },
];

export const HowItWorks = () => {
    const { t } = useTranslation("common");

    return (
        <section id="how" className="py-20 sm:py-28 bg-background-elevated">
            <div className="container mx-auto px-4 sm:px-6">
                <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
                    <p className="text-xs font-semibold tracking-[0.2em] uppercase text-accent-primary mb-3">
                        {t("landing.how.eyebrow")}
                    </p>
                    <h2 className="text-3xl sm:text-4xl font-bold font-display text-text-primary mb-4">
                        {t("landing.how.title")}
                    </h2>
                    <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400">
                        {t("landing.how.subtitle")}
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6 lg:gap-8 relative">
                    {/* Connecting line on desktop */}
                    <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-accent-primary/30 to-transparent" aria-hidden />

                    {STEPS.map((step, i) => (
                        <motion.div
                            key={step.titleKey}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.45, delay: i * 0.1 }}
                            className="relative bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-6 sm:p-7 hover:border-accent-primary/40 transition-colors"
                        >
                            <div className="flex items-center gap-3 mb-5">
                                <div className="w-12 h-12 rounded-xl bg-accent-primary/10 text-accent-primary flex items-center justify-center shrink-0">
                                    {step.iconNode}
                                </div>
                                <span className="text-sm font-mono text-neutral-400">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                            </div>
                            <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">
                                {t(step.titleKey)}
                            </h3>
                            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed mb-4">
                                {t(step.descKey)}
                            </p>
                            <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-500 border-t border-neutral-100 dark:border-neutral-800 pt-3 mt-auto">
                                {t(step.detailKey)}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
